import React from "react"
import { useRecoilState, useSetRecoilState } from "recoil"
import { Formik, Form } from "formik"
import * as Yup from "yup"
import { useSnackbar } from "notistack"
import { Auth } from "aws-amplify"
import Grid from "@material-ui/core/Grid"

import PasswordField from "../components/PasswordField"
import SelectField from "../components/SelectField"
import StepButtons from "../components/StepButtons"
import TextField from "../components/TextField"
import { createCognitoUser } from "../libs/manageAccounts"
import positionOptions from "../libs/positions"
import onboardingProgress from "../store/onboardingProgress"
import currentUser from "../store/currentUser"

export default function UserInfo() {
  const [progress, setProgress] = useRecoilState(onboardingProgress)
  const setUser = useSetRecoilState(currentUser)
  const { enqueueSnackbar } = useSnackbar()

  const initialValues = {
    email: progress.invite.email,
    firstName: progress.user.firstName,
    lastName: progress.user.lastName,
    password: progress.user.password,
    confirmPassword: progress.user.confirmPassword,
  }

  const validationSchema = {
    email: Yup.string().email().required("Email required"),
    firstName: Yup.string().required("First name required"),
    lastName: Yup.string().required("Last name required"),
    password: Yup.string()
      .required("Password required")
      .matches(
        /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/,
        "Must be minimum 8 characters, 1 uppercase, 1 lowercase, 1 number, and 1 special character"
      ),
    // confirmPassword: Yup.string().oneOf([Yup.ref("password"), null], "Passwords must match"),
    confirmPassword: Yup.string().test("passwords-match", "Passwords must match", function (value) {
      return this.parent.password === value
    }),
  }

  if (progress.invite) {
    if (progress.invite.role === "BOARD") {
      initialValues.position = progress.user.position
      validationSchema.position = Yup.string()
        .oneOf(positionOptions.map((position) => position.value))
        .required("Please provide your position")
    }
    initialValues.unit = progress.user.unit
    validationSchema.unit = Yup.string().required("Unit required")
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={Yup.object().shape(validationSchema)}
      onSubmit={async (values) => {
        const newObj = {
          ...progress,
          user: {
            ...progress.user,
            email: values.email,
            firstName: values.firstName,
            lastName: values.lastName,
            password: values.password,
            confirmPassword: values.confirmPassword,
          },
        }
        try {
          const userInfo = await createCognitoUser(newObj.user)
          if (userInfo.user) {
            newObj.verify = true
            newObj.user.id = userInfo.userSub
            enqueueSnackbar("Success! Verify your email")
            setProgress({
              ...progress,
              activeStep: progress.activeStep + 1,
            })
            setUser(newObj.user)
          } else {
            enqueueSnackbar(userInfo.message)
            if (userInfo.code === "UsernameExistsException") {
              newObj.verify = true
              // const currentUserInfo =
              await Auth.currentUserInfo()
            }
          }
        } catch (error) {
          console.log("error", error)
          enqueueSnackbar(error.message, {
            variant: "error",
          })
        }
        // setProgress(newObj)
      }}
    >
      {({ errors, handleBlur, isSubmitting, isValid, touched, values, setFieldValue }) => {
        const handleChange = (event) => {
          setFieldValue(event.target.name, event.target.value)
          setProgress({
            ...progress,
            user: {
              ...progress.user,
              [event.target.name]: event.target.value,
            },
          })
        }
        return (
          <Form>
            <Grid container spacing={3}>
              <Grid item md={6}>
                <TextField
                  autoFocus
                  error={Boolean(touched.firstName && errors.firstName)}
                  helperText={touched.firstName && errors.firstName}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.firstName}
                  label="First Name"
                  name="firstName"
                />
              </Grid>
              <Grid item md={6}>
                <TextField
                  error={Boolean(touched.lastName && errors.lastName)}
                  helperText={touched.lastName && errors.lastName}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.lastName}
                  label="Last Name"
                  name="lastName"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  error={Boolean(touched.email && errors.email)}
                  helperText={touched.email && errors.email}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.email}
                  label="Your Email"
                  name="email"
                />
              </Grid>
              {progress.invite ? (
                <>
                  {progress.invite.role === "BOARD" ? (
                    <Grid item xs={12}>
                      <SelectField
                        error={Boolean(touched.position && errors.position)}
                        helperText={touched.position && errors.position}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.position}
                        label="Your Position"
                        name="position"
                        options={positionOptions}
                      />
                    </Grid>
                  ) : (
                    ""
                  )}
                  <Grid item xs={12}>
                    <TextField
                      error={Boolean(touched.unit && errors.unit)}
                      helperText={touched.unit && errors.unit}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.unit}
                      label="Your Unit Number"
                      name="unit"
                    />
                  </Grid>
                </>
              ) : (
                ""
              )}
              <Grid item xs={6}>
                <PasswordField
                  error={Boolean(touched.password && errors.password)}
                  helperText={touched.password && errors.password}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.password}
                  label="Password"
                  name="password"
                />
              </Grid>
              <Grid item xs={6}>
                <PasswordField
                  error={Boolean(touched.confirmPassword && errors.confirmPassword)}
                  helperText={touched.confirmPassword && errors.confirmPassword}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.confirmPassword}
                  label="Confirm Password"
                  name="confirmPassword"
                />
              </Grid>
            </Grid>
            <Grid container spacing={3} justifyContent="flex-end">
              <Grid item xs={12}>
                <StepButtons disableNext={!isValid} isLoading={isSubmitting} />
              </Grid>
            </Grid>

            <div style={{ clear: "both", content: "", display: "block" }}></div>
          </Form>
        )
      }}
    </Formik>
  )
}
